import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/config/api";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Search, ShoppingCart } from "lucide-react";

const PAGE_SIZE = 10;

export default function Leads() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [orders, setOrders] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [assigningId, setAssigningId] = useState(null);
  const [page, setPage] = useState(1);

  const fetchOrders = () => {
    setLoading(true);
    api.orders
      .getAll()
      .then(setOrders)
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      api.users.getAll().then(setTeamMembers).catch(() => setTeamMembers([]));
    }
  }, [isAdmin]);

  const salesTeam = useMemo(
    () => (teamMembers || []).filter((u) => u.role === "sales" || u.role === "admin"),
    [teamMembers],
  );

  const filtered = useMemo(() => {
    let list = [...(orders || [])];
    const s = (search || "").toLowerCase().trim();
    if (s) {
      list = list.filter(
        (o) =>
          (o.customer_name || "").toLowerCase().includes(s) ||
          (o.assigned_to_name || "").toLowerCase().includes(s) ||
          (o.created_by_name || "").toLowerCase().includes(s),
      );
    }
    if (statusFilter) {
      list = list.filter((o) => (o.status || "").toLowerCase() === statusFilter.toLowerCase());
    }
    return list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [orders, search, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  const handleAssign = async (orderId, assignedTo) => {
    setAssigningId(orderId);
    try {
      await api.orders.assign(orderId, assignedTo || null);
      setOrders((prev) =>
        prev.map((o) => {
          if (o.id !== orderId) return o;
          const u = salesTeam.find((x) => x.id === assignedTo);
          return { ...o, assigned_to: assignedTo || null, assigned_to_name: u ? u.name : null };
        }),
      );
    } catch (err) {
      console.error(err);
    } finally {
      setAssigningId(null);
    }
  };

  if (loading && orders.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div className="min-w-0 max-w-full">
          <h1 className="text-2xl font-heading font-semibold text-secondary">Leads</h1>
          <p className="form-helper mt-1 max-w-full">Sales funnel: orders are your leads. Open = in progress, Closed = converted, Cancelled = lost. The person who creates an order is the default assignee; admins can reassign.</p>
        </div>
        <Link
          to="/orders/new"
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
        >
          <Plus size={18} />
          New order (lead)
        </Link>
      </div>

      {/* Search and filter */}
      <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <input
              type="text"
              placeholder="Search by customer or assignee..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full pl-9 pr-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
            />
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => {
                setStatusFilter("");
                setPage(1);
              }}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                !statusFilter ? "bg-primary text-white" : "bg-stone-100 text-stone-700"
              }`}
            >
              All
            </button>
            <button
              type="button"
              onClick={() => {
                setStatusFilter("open");
                setPage(1);
              }}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                statusFilter === "open" ? "bg-primary text-white" : "bg-stone-100 text-stone-700"
              }`}
            >
              Open
            </button>
            <button
              type="button"
              onClick={() => {
                setStatusFilter("closed");
                setPage(1);
              }}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                statusFilter === "closed" ? "bg-primary text-white" : "bg-stone-100 text-stone-700"
              }`}
            >
              Closed
            </button>
            <button
              type="button"
              onClick={() => {
                setStatusFilter("cancelled");
                setPage(1);
              }}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
                statusFilter === "cancelled" ? "bg-primary text-white" : "bg-stone-100 text-stone-700"
              }`}
            >
              Cancelled
            </button>
          </div>
        </div>
        <p className="form-helper mt-2">
          Showing {filtered.length} of {orders.length} order(s)
        </p>
      </div>

      <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Customer</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Status</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Assigned to</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Created by</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-stone-600">Date</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-stone-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-stone-500">
                    No orders yet. Create an order to start your sales funnel.
                  </td>
                </tr>
              ) : (
                paginated.map((order) => (
                  <tr key={order.id} className="border-b border-stone-100 hover:bg-stone-50/50">
                    <td className="py-3 px-4">
                      <span className="font-medium text-secondary">{order.customer_name}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                          order.status === "closed"
                            ? "bg-emerald-100 text-emerald-700"
                            : order.status === "cancelled"
                              ? "bg-rose-100 text-rose-700"
                              : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {order.status || "open"}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-sm text-stone-600">
                      {isAdmin ? (
                        <select
                          value={order.assigned_to || ""}
                          onChange={(e) => handleAssign(order.id, e.target.value || null)}
                          disabled={assigningId === order.id}
                          className="text-sm border border-stone-300 rounded px-2 py-1.5 min-w-[120px]"
                        >
                          <option value="">Unassigned</option>
                          {salesTeam.map((u) => (
                            <option key={u.id} value={u.id}>
                              {u.name}
                            </option>
                          ))}
                        </select>
                      ) : (
                        order.assigned_to_name || "—"
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm text-stone-600">
                      {order.created_by_name || "—"}
                    </td>
                    <td className="py-3 px-4 text-sm text-stone-600">
                      {order.created_at
                        ? new Date(order.created_at).toLocaleDateString(undefined, {
                            dateStyle: "short",
                          })
                        : "—"}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <Link
                        to={`/orders/${order.id}`}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/10 rounded-lg"
                      >
                        <ShoppingCart size={14} />
                        View order
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-stone-200 bg-stone-50/50">
            <p className="text-sm text-stone-600">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1.5 rounded-lg border border-stone-300 hover:bg-stone-100 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 rounded-lg border border-stone-300 hover:bg-stone-100 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
